import React from 'react';

import { ItemsProvider } from '@common/react/components/Core/ItemsProvider/ItemsProvider';

import {
	blockRichText,
	creationStorySlidesContent,
	linkMoreCaption,
	mastHeadText,
	navItems,
	videoSrc,
	videoText,
	videoTitle,
	whoBlockText,
	whoBlockTitle,
	whyBlockText,
	whyBlockTitle,
} from '@app/components/Pages/AboutUs/AboutUsContent';
import VetsMap from '@app/components/Pages/AboutUs/VetsMap';
import FiftyFifty from '@app/components/UI/FiftyFifty/FiftyFifty';
import FoundersBlock from '@app/components/UI/FoundersBlock/FoundersBlock';
import HelmetProvider from '@app/providers/HelmetProvider/HelmetProvider';
import BlockRich from '@app/components/UI/LinkBlock/BlockRich';
import Masthead from '@app/components/UI/MastHead/Masthead';
import NavTabs from '@app/components/UI/NavTabs/NavTabs';
import CreationStorySlider from '@app/components/UI/Slider/CreationStorySlider';
import VideoEmbed from '@app/components/UI/VideoEmbed/VideoEmbed';
import { useIsMobile } from '@app/hooks/useIsMobile';
import aboutUsDrDani from '@app/images/content/aboutUsDrDani.jpg';
import aboutUsDrMary from '@app/images/content/aboutUsDrMary.jpg';
import careCoordinators from '@app/images/content/coordinators.png';
import daniAndMary from '@app/images/content/Dani-and-Mary.jpg';
import daniAndMaryMobile from '@app/images/content/mobile/Dani-and-Mary.jpg';
import avma from '@app/images/content/organizations/avma.gif';
import bbb from '@app/images/content/organizations/BBBlogo.png';
import caeta from '@app/images/content/organizations/CPEPracticeCAETA.png';
import dvm360 from '@app/images/content/organizations/dvm360.jpg';
import habri from '@app/images/content/organizations/habri.jpg';
import iaahpc from '@app/images/content/organizations/IAAHPC.png';
import nomv from '@app/images/content/organizations/NOMVProudSupporter-logo.png';
import vetangel from '@app/images/content/organizations/vet-angel-logo.png';
import whoBlockImg from '@app/images/content/whoBlockImg.png';
import { Veterinarian } from '@app/objects/Veterinarian';
import CustomRequestProvider from '@app/providers/CustomRequestProvider/CustomRequestProvider';
import '@app/scss/components/aboutUs.scss';

interface Organization {
	src: string;
	link: string;
	alt: string;
}

const organizations: Array<Organization> = [
	{
		src: vetangel,
		link: 'https://www.vetangel.com/',
		alt: 'Vet Angel - logo',
	},
	{
		src: dvm360,
		link: 'https://www.dvm360.com/',
		alt: 'DVM360 - logo',
	},
	{
		src: avma,
		link: 'https://www.avma.org/',
		alt: 'American Veterinary Medical Association - logo',
	},
	{
		src: iaahpc,
		link: 'https://iaahpc.org/',
		alt: 'International Association for Animal Hospice and Palliative Care - logo',
	},
	{
		src: caeta,
		link: 'https://caetainternational.com/practice-certification/',
		alt: 'Caeta Certified Peaceful End-of-Life Practice - logo',
	},
	{
		src: habri,
		link: 'https://habri.org/',
		alt: 'Habri - logo',
	},
	{
		src: nomv,
		link: 'https://nomv.org/',
		alt: 'NOMV Proud Supporter - logo',
	},
	{
		src: bbb,
		link: 'https://www.bbb.org/',
		alt: 'Better Business Bureau - logo',
	},
];

const AboutUs: React.FC = () => {
	const blockRichTitle = (
		<>
			It's a
			{' '}
			<span className="highlight">calling</span>
			{' '}
			as much as it is a career
		</>
	);

	const [currentIndex, setCurrentIndex] = React.useState(0);
	const navCollapse = useIsMobile();

	const [stateName, setStateName] = React.useState('');

	const whoRef = React.useRef<any>();
	const whyRef = React.useRef<any>();
	const foundersRef = React.useRef<any>();
	const joinRef = React.useRef<any>();
	const organizationsRef = React.useRef<any>();

	const onSetIssue = (index) => {
		const refs = [whoRef, whyRef, foundersRef, joinRef, organizationsRef];
		let num = index;

		if (navCollapse) {
			navItems.forEach((item, i: number) => {
				if (item.title === index) {
					num = i;
				}
			});
		}

		setCurrentIndex(num);
		refs?.[num]?.current?.scrollIntoView({ block: 'start' });
	};

	return (
		<HelmetProvider>
			<div className="about-us">
				<Masthead
					title="About Lap of Love"
					imageSrc={daniAndMary}
					mobileSrc={daniAndMaryMobile}
					text={mastHeadText}
					quoteAuthor="Lap of Love Hospice Veterinarian"
					imageAlt="Lap of Love veterinarians"
				/>
				<NavTabs navItems={navItems} currentIndex={currentIndex} onSetIssue={onSetIssue} navCollapse={navCollapse} onChange={onSetIssue} />

				<CustomRequestProvider>
					<ItemsProvider<Veterinarian>
						type={`veterinarians/${stateName}`}
						loadRequest={`veterinarians/${stateName}`}
						pagination={{ total: 0, pageSize: 30 }}
					>
						<VetsMap
							stateName={stateName}
							setStateName={setStateName}
						/>
					</ItemsProvider>
				</CustomRequestProvider>

				<FiftyFifty
					title={whoBlockTitle}
					text={whoBlockText}
					src={whoBlockImg}
					name={navItems[0].name}
					alt="Euthanasia veterinarian"
					refFiftyFifty={whoRef}
				/>
				<CreationStorySlider slides={creationStorySlidesContent} navCollapse={navCollapse} />
				<VideoEmbed text={videoText} videoSrc={videoSrc} videoTitle={videoTitle} />
				<FiftyFifty
					title={whyBlockTitle}
					text={whyBlockText}
					src={whoBlockImg}
					reversed
					alt="Dr. Mary Gardner"
					name={navItems[1].name}
					refFiftyFifty={whyRef}
				/>
				<FoundersBlock name={navItems[2].name} foundersRef={foundersRef} />
				<BlockRich
					title={blockRichTitle}
					text={blockRichText}
					images={[
						{
							title: 'Dr. Mary',
							src: aboutUsDrMary,
							alt: 'At home euthanasia',
						},
						{
							title: 'Dr. Dani',
							src: aboutUsDrDani,
							alt: 'Pet hospice',
							width: 317,
						},
						{
							title: 'Care coordinators loving their job!',
							src: careCoordinators,
							alt: 'Lap of love support',
							width: 317,
						},
					]}
					linkMoreCaption={linkMoreCaption}
					subtitle="Join our team"
					name={navItems[3].name}
					refBlockRich={joinRef}
				/>
				<BlockRich title="Proud member of the following organizations" subtitle="Organizations" refBlockRich={organizationsRef} />
				<div className="organizations">
					{organizations.map((organization, i) => (
						<div key={i} className="organizations-item">
							<a href={organization.link} target="_blank" rel="noopener noreferrer">
								<img src={organization.src} alt={organization.alt} />
							</a>
						</div>
					))}
				</div>
			</div>
		</HelmetProvider>
	);
};

export default AboutUs;
